import RightPanelCard from "components/Common/RightPanelCard";
import React, { useEffect, useState, useRef, useMemo } from "react";
import {
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Col,
  FormGroup,
  Label,
  Row,
} from "reactstrap";
import Select from "react-select";
import { useDispatch, useSelector } from "react-redux";
import { customStyle } from "components/Common/constants";
import DatePicker from "react-datepicker";
import { getTechniciansList } from "actions/technicians";
import { getAvailableDates } from "actions/availableTechnicianDates";
import {
  formatDateLocal,
  formatDateWithTZ,
} from "components/Common/commonFunctions";
import { addRescheduleJobById } from "actions/availableTechnicianDates";
import { quotationRequoteList } from "actions/jobQuotation";
import { WORK_ORDER, SMALL_CASE_WORK_ORDER } from "components/Common/constants";

const initialFields = {
  timeSlot: {
    value: "",
    error: "",
    isChanged: false,
  },
  startDateTime: {
    value: null,
    error: "",
    isChanged: false,
  },
  technician: {
    value: null,
    error: "",
    isChanged: false,
  },
  reason: {
    value: "",
    error: "",
    isChanged: false,
  },
};
export function RescheduleJob({
  isOpen,
  handleClose,
  technicianID,
  decodedId,
  onRescheduleSuccess,
  notify,
  jobSchedules,
}) {
  //get the technician id for auto fill name
  const dispatch = useDispatch();
  const technicianListData = useSelector(
    (state) => state.technicians.techniciansList,
  );
  const getAvailableSlotsData = useSelector(
    (state) => state.availableDates.availableTechnicianDates,
  );
  const [jobScheduledData, setJobScheduledData] = useState(initialFields);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [showLoaderOnClick, setShowLoaderOnClick] = useState(false);
  const [requoteReasons, setRequoteReasons] = useState([]);

  //to memroized the available dates for particular technician based on id
  const availableDates = useMemo(() => {
    return (getAvailableSlotsData?.data?.days || []).map((d) => {
      const [day, month, year] = d.date.split("-"); //"15-12-2025" -> day = "15", month = "12", year = "2025"
      return formatDateLocal(new Date(year, month - 1, day)); // new Date(2025, 11, 15)
      //convert into yyyy/mm/dd as datepicker need
    });
  }, [getAvailableSlotsData?.data?.days]);

  const previousSchedule = jobSchedules
    ? jobSchedules[jobSchedules?.length - 1]
    : [];

  //get the technician list
  useEffect(() => {
    getTechniciansList(dispatch);
  }, [dispatch]);

  useEffect(() => {
    if (
      isOpen &&
      technicianID &&
      technicianListData?.data?.technician?.length > 0
    ) {
      const selectedTechnician = technicianListData.data.technician.find(
        (tech) => tech.ID === technicianID,
      );

      if (selectedTechnician) {
        setJobScheduledData((prev) => ({
          ...prev,
          technician: {
            ...prev.technician,
            value: selectedTechnician,
            error: "",
          },
        }));
      }
    }
    getReasonList();
  }, [isOpen, technicianID, technicianListData]);

  const getReasonList = async () => {
    try {
      let filtersJSON = [
        {
          field: "ReasonType",
          condition: "eq",
          filterValues: ["RESCHEDULE"],
        },
      ];

      const res = await quotationRequoteList({
        filtersJSON: JSON.stringify(filtersJSON),
      });
      if (res?.code === 200) {
        // let array = [];
        // let requoteReasons = res?.data.reasons;
        // for (let i = 0; i < requoteReasons?.length; i++) {
        //   array.push({
        //     ID: requoteReasons[i].ID,
        //     name: requoteReasons[i].name,
        //   });
        // }
        setRequoteReasons(res?.data.reasons);
      }
    } catch (error) {
      console.log("error", error);
    }
  };

  useEffect(() => {
    const technicianId = jobScheduledData.technician.value?.ID;
    if (technicianId) {
      getAvailableDates(dispatch, technicianId);
    }
  }, [jobScheduledData.technician.value]);

  useEffect(() => {
    //get the current selected date from datepicker
    const selectedDate = jobScheduledData.startDateTime.value;
    if (!selectedDate) {
      setAvailableSlots([]);
      return;
    }
    const formattedDate = selectedDate
      .toLocaleDateString("en-GB") //15/12/2025
      .split("/") // ["15", "12", "2025"]
      .join("-"); //15-12-2025

    const dayData = getAvailableSlotsData?.data?.days?.find(
      (d) => d.date === formattedDate,
    );

    const slots = (dayData?.slots || []).map((slot) => ({
      value: `${slot.fromTime}-${slot.toTime}`,
      label: `${slot.fromTime.slice(0, 5)} - ${slot.toTime.slice(0, 5)}`,
      ...slot,
    }));

    setAvailableSlots(slots);
  }, [jobScheduledData.startDateTime.value, getAvailableSlotsData]);

  const handleChange = (fieldName, newValue) => {
    setJobScheduledData((prev) => ({
      ...prev,
      [fieldName]: {
        ...prev[fieldName],
        value: newValue,
        isChanged: true,
        error: "",
      },
      ...(fieldName === "startDateTime" && {
        timeSlot: { value: "", error: "", isChanged: false },
      }),
    }));
  };

  const handleOnClose = () => {
    handleClose();
    setJobScheduledData(initialFields); //set to initial value after close modal
  };

  const getValidationError = (name, value) => {
    switch (name) {
      case "technician":
        return !value ? "Please select a technician" : "";
      case "reason":
        return !value ? "Please select a reason" : "";
      case "startDateTime":
        if (!value) {
          return `Please select a date to reschedule the ${SMALL_CASE_WORK_ORDER} to technician`;
        }
        // Check if there are available dates for this technician
        if (!availableDates || availableDates.length === 0) {
          return "No available dates for this technician. Please choose another technician";
        }
        return "";
      case "timeSlot":
        return !value ? "Please select an available time slot" : "";
      default:
        return "";
    }
  };

  const validateForm = () => {
    let newScheduledJob = { ...jobScheduledData };

    //get all the keys in array
    Object.keys(newScheduledJob).forEach((key) => {
      newScheduledJob[key] = {
        ...newScheduledJob[key],
        error: getValidationError(key, newScheduledJob[key].value),
      };
    });
    setJobScheduledData(newScheduledJob); //if error set error state
    return Object.values(newScheduledJob).every((item) => item.error === "");
  };

  const handleOnBlur = (name, value) => {
    const trimmedValue = typeof value === "string" ? value.trim() : value;
    const error = getValidationError(name, trimmedValue);
    setJobScheduledData((prev) => ({
      ...prev,
      [name]: {
        ...prev[name],
        value: trimmedValue,
        error,
      },
    }));
  };

  const handleSaveClick = async () => {
    //validate the form
    if (!validateForm()) return;
    try {
      setShowLoaderOnClick(true);
      // Split the selected timeslot into fromTime and toTime
      const [fromTime, toTime] = jobScheduledData.timeSlot.value.split("-");

      // Create Date objects for the selected date
      const scheduledFrom = new Date(jobScheduledData.startDateTime.value);
      const scheduledTo = new Date(jobScheduledData.startDateTime.value);

      // Set the hours, minutes, seconds from the slot and convert string to number
      const [fromH, fromM, fromS] = fromTime.split(":").map(Number);
      const [toH, toM, toS] = toTime.split(":").map(Number);

      scheduledFrom.setHours(fromH, fromM, fromS, 0); // Mon Dec 15 2025 02:00:00
      scheduledTo.setHours(toH, toM, toS, 0); //Mon Dec 15 2025 04:00:00

      // Function to format date
      const formatDateTime = (date) => {
        return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")} ${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}:${String(date.getSeconds()).padStart(2, "0")}`;
      };

      const bodyData = {
        //need to format as backend req 2025-01-10T15:42:25.000Z
        scheduledFrom: formatDateTime(scheduledFrom), //2025-12-15T12:35:16Z
        scheduledTo: formatDateTime(scheduledTo), //2025-12-15T12:35:16Z
        rescheduleReasonId: 2, // static for now
        technicianID: jobScheduledData.technician.value.ID,
      };
      const res = await addRescheduleJobById(decodedId, bodyData);
      if (res.code === 200) {
        notify("success", res?.message || `${WORK_ORDER} Reschedule Successfully`);

        //if sucessfully created -> then refresh list timeline
        onRescheduleSuccess?.();
        setTimeout(() => {
          handleOnClose();
        }, 1000);
      } else {
        const message = Array.isArray(res?.message)
          ? res?.message[0]?.Msg
          : res?.message || `Failed to Reschedule ${SMALL_CASE_WORK_ORDER}`;
        notify("danger", message);
      }
    } catch (error) {
      notify("danger", `Failed to reschedule ${SMALL_CASE_WORK_ORDER} for selected technician!`);
    } finally {
      setShowLoaderOnClick(false);
    }
  };
  return (
    <>
      <RightPanelCard isOpen={isOpen} onClose={handleOnClose}>
        <Card className="location-card mb-0">
          <CardHeader
            className="d-flex align-items-center justify-content-between"
            tag="h4"
          >
            Reschedule {WORK_ORDER}
            <i
              className="fa fa-close text-muted pointer"
              onClick={handleOnClose}
            />
          </CardHeader>
          <hr />

          <CardBody className="scrollbar-inner overflow-auto">
            <Row className="mb-4">
              <Col className="user-note px-3 py-2 mx-3 rounded shadow-sm">
                <span className="">
                  {WORK_ORDER} Scheduled Date & Time :
                  <span className="ml-1">
                    {previousSchedule?.scheduledFrom} to{" "}
                    {previousSchedule?.scheduledTo}
                  </span>
                </span>
              </Col>
            </Row>
            {jobScheduledData?.technician?.value && availableDates?.length === 0 &&
              <div className="mb-4">
                <span className="validation-error">
                  No new date available for {jobScheduledData?.technician?.value?.fullName}. Please set the technician’s availability or select another technician.
                </span>
              </div>
            }
            <Row>
              <Col lg="6" className="mb-3">
                <FormGroup
                  className={`has-label ${jobScheduledData.technician.error ? "has-danger" : ""
                    } d-flex flex-column align-items-start`}
                >
                  {" "}
                  <Label>
                    Technician <span className="validation-error">*</span>
                  </Label>
                  <Select
                    className={`text-left ${jobScheduledData.technician.error ? "is-invalid" : ""
                      }`}
                    placeholder="Select a technician"
                    classNamePrefix="react-select"
                    styles={customStyle}
                    options={technicianListData?.data?.technician || []}
                    value={jobScheduledData.technician.value}
                    getOptionLabel={(options) => options.fullName}
                    getOptionValue={(options) => options.ID}
                    onChange={(selected) =>
                      handleChange("technician", selected)
                    }
                    onBlur={(e) =>
                      handleOnBlur(
                        "technician",
                        jobScheduledData.technician.value,
                      )
                    }
                    menuPortalTarget={document.body}
                    menuPosition={"fixed"}
                  />
                  {jobScheduledData.technician.error && (
                    <small className="validation-error">
                      {jobScheduledData.technician.error}
                    </small>
                  )}
                </FormGroup>
              </Col>

              <Col lg="6" className="mb-3">
                <FormGroup
                  className={`has-label ${jobScheduledData.startDateTime.error ? "has-danger" : ""
                    } d-flex flex-column align-items-start`}
                >
                  <Label>
                    New Date <span className="validation-error">*</span>
                  </Label>
                  <DatePicker
                    placeholderText="Select a new date"
                    selected={jobScheduledData.startDateTime.value}
                    onChange={(date) => handleChange("startDateTime", date)}
                    dateFormat="dd MMM yyyy"
                    className="form-control cust-datepicker"
                    showIcon
                    toggleCalendarOnIconClick
                    icon={<i className="fa-solid fa-calendar-day fa-md mt-1" />}
                    wrapperClassName="w-100"
                    minDate={new Date()}
                    popperPlacement="bottom-start"
                    popperContainer={({ children }) => (
                      <div className="fixed-popper-container">{children}</div>
                    )}
                    filterDate={(date) =>
                      availableDates.includes(formatDateLocal(date))
                    }
                    onBlur={(e) =>
                      handleOnBlur(
                        "startDateTime",
                        jobScheduledData.startDateTime.value,
                      )
                    }
                  />
                  {jobScheduledData.startDateTime.error && (
                    <small className="validation-error">
                      {jobScheduledData.startDateTime.error}
                    </small>
                  )}
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col lg="6" className="mb-4">
                <Label className="mb-2">
                  Available Time Slots <span className="validation-error">*</span>
                </Label>
                <div
                  className={`px-3 py-2 time-slot-card rounded border ${jobScheduledData.timeSlot.error ? "has-danger" : ""
                    }`}
                  tabIndex={0}
                  onBlur={() =>
                    handleOnBlur("timeSlot", jobScheduledData.timeSlot.value)
                  }
                >
                  {!jobScheduledData.startDateTime.value ? (
                    <span className="no-record-card">
                      Please select a new date to view available time slots.
                    </span>
                  ) : availableSlots.length > 0 ? (
                    availableSlots.map((slot, idx) => (
                      <label
                        key={idx}
                        className={`reason-card ${jobScheduledData.timeSlot.value === slot.value
                          ? "selected"
                          : ""
                          }`}
                      >
                        <input
                          type="radio"
                          name="timeSlot"
                          value={slot.value}
                          checked={
                            jobScheduledData.timeSlot.value === slot.value
                          }
                          onChange={() => handleChange("timeSlot", slot.value)}
                          className="reason-radio"
                        />
                        <span className="reason-text">{slot.label}</span>
                      </label>
                    ))
                  ) : (
                    <span className="no-record-card">
                      No available time slots for this date.
                    </span>
                  )}
                </div>
                {jobScheduledData.timeSlot.error && (
                  <small className="validation-error d-block mt-1">
                    {jobScheduledData.timeSlot.error}
                  </small>
                )}
              </Col>
              <Col lg="6" className="mb-3">
                <FormGroup
                  className={`has-label ${jobScheduledData.reason.error ? "has-danger" : ""
                    } d-flex flex-column align-items-start w-100`}
                >
                  <Label className="mb-2">
                    Reason to reschedule this {SMALL_CASE_WORK_ORDER}{" "}
                    <span className="validation-error">*</span>
                  </Label>
                  <Select
                    className={`text-left ${jobScheduledData.reason.error ? "is-invalid" : ""
                      }`}
                    options={requoteReasons}
                    placeholder="Select a reason"
                    classNamePrefix="react-select"
                    styles={customStyle}
                    value={jobScheduledData.reason.value}
                    getOptionLabel={(options) => options.name}
                    getOptionValue={(options) => options.ID}
                    onChange={(selected) => handleChange("reason", selected)}
                    onBlur={(e) =>
                      handleOnBlur("reason", jobScheduledData.reason.value)
                    }
                    menuPortalTarget={document.body}
                    menuPosition={"fixed"}
                  />
                  {jobScheduledData.reason.error && (
                    <small className="validation-error">
                      {jobScheduledData.reason.error}
                    </small>
                  )}
                </FormGroup>
              </Col>
            </Row>
          </CardBody>

          <CardFooter className="d-flex justify-content-end mb-0 mt-4">
            <Button color="secondary" onClick={handleOnClose} className="mr-2">
              Cancel
            </Button>
            <Button
              color="default"
              type="submit"
              onClick={() => {
                handleSaveClick();
              }}
              disabled={Object.values(jobScheduledData).some(
                (item) => item.error !== "",
              )}
            >
              {showLoaderOnClick ? (
                <i className="fa fa-spinner fa-spin fa-lg text-white" />
              ) : (
                "Save"
              )}
            </Button>
          </CardFooter>
        </Card>
      </RightPanelCard>
    </>
  );
}
